import { useEffect, useState } from 'react';
import type { BibleLocation } from '@skjv/common';
import { getVerseText } from '@/services/verse-text';

interface VersePreviewProps {
  /** Starting verse location */
  location: BibleLocation;
  /** Optional ending verse location for ranges */
  endingLocation?: BibleLocation;
  /** CSS class for styling the text container */
  className?: string;
  /** Number of lines to clamp (default: 2) */
  lineClamp?: 1 | 2 | 3 | 4;
}

/**
 * Reusable component that displays a preview of verse text.
 * Fetches verse text from the chapter HTML files and shows a truncated preview.
 */
export function VersePreview({
  location,
  endingLocation,
  className = '',
  lineClamp = 2,
}: VersePreviewProps) {
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadText = async () => {
      setLoading(true);
      try {
        const verseText = await getVerseText(location, endingLocation);
        if (!isCancelled) {
          setText(verseText);
        }
      } catch {
        if (!isCancelled) {
          setText('');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadText();

    return () => {
      isCancelled = true;
    };
  }, [
    location.testamentIndex,
    location.bookIndex,
    location.chapterIndex,
    location.verseIndex,
    endingLocation?.testamentIndex,
    endingLocation?.bookIndex,
    endingLocation?.chapterIndex,
    endingLocation?.verseIndex,
  ]);

  if (loading || !text) {
    return null;
  }

  const clampClass = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
  }[lineClamp];

  return <p className={`${clampClass} ${className}`}>{text}</p>;
}
